import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Formik } from "formik";
import { Drawer, IconButton as MuiIconButton, Stack, styled } from "@mui/material";
import ContactForm from "../organizations/components/contact-form/ContactForm";
import RestaurantForm from "./components/RestaurantForm";
import { useAppDispatch, useAppSelector, useOrder, useResponsive } from "@/hooks";
import { closeItem, openItem } from "../../store/uiSlice";
import { toggleMenuItemAvailability } from "../organizations/slice";
import { useFetchContentSettingsQuery } from "../organizations/api/settingsApi";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { v4 } from "uuid";
import { Content, OrderItem, Setting, Option } from "@/types";
import useDishNavigation from "@/hooks/useDishNavigation";
import { MenuContext } from "@/pages/menu/Menu";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon, ViewIcon, ViewOffIcon } from "@hugeicons/core-free-icons";
import { useUpdateContentMutation } from "@/features/organizations/api/contentApi";
import { isLoggedInSelector } from '@/features/auth/slice';

export interface SelectedSettings {
  [key: Setting["title"]]: Option["title"][];
}

interface Props {
  enableOrders?: boolean;
  onClose?: () => void;
}

export interface FormValues {
  quantity: number;
  settings: Record<string, string>;
  comments?: string;
}

const IconButton = styled(MuiIconButton)(() => ({
  backgroundColor: "#ffffff",
  boxShadow: "0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)",
  "&:hover": {
    backgroundColor: "#00000080",
    color: "#ffffff",
  },
}));

const ProductDetails = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organization } = useContext(WebsiteContext);
  const organizationId = useAppSelector((state) => state.ui.activeOrganization);
  const selectedMenuItem = useAppSelector((state) => state.ui?.selectedMenuItem) as unknown as Content;
  const [isLoading, setIsLoading] = useState(false);
  const orgId = organization ? organization.id : organizationId;
  const activeBranch = useAppSelector((store) => store.ui.activeBranch);

  const { menu, template } = useContext(MenuContext);
  const content = useAppSelector((state) => state.organizations?.[orgId]?.content as Content[]);
  const publishedContent = content?.filter((c) => c.status === "published" && !c?.branchSettings?.[activeBranch]?.isDisabled) || [];

  const [updateContent, updateContentMutation] = useUpdateContentMutation();
  const isLoggedIn = useAppSelector(isLoggedInSelector);

  const { onContinue, onBack } = useDishNavigation({
    structure: menu?.structure ?? template?.structure ?? [],
    content: publishedContent ?? [],
  });

  const contentSettings = useFetchContentSettingsQuery({
    organizationId: orgId ?? skipToken,
    contentId: selectedMenuItem?.id ?? skipToken,
  });

  const { addItems } = useOrder();
  const { isMobile } = useResponsive();

  const handleClose = () => {
    const qs = new URLSearchParams(document.location.search);
    qs.delete("itemId");

    const url = document.location.pathname + '?' + qs.toString();
    navigate(url);

    dispatch(closeItem());
    onClose?.();
  };

  const toggleAvailability = (event: ChangeEvent<HTMLInputElement>) => {
    if (!organizationId || !selectedMenuItem?.id) return;

    dispatch(
      toggleMenuItemAvailability({
        organizationId: orgId,
        menuItemId: selectedMenuItem?.id,
        availability: event.target.checked,
      })
    );
  };

  const onSubmit = async (formValues: FormValues) => {
    if (!organizationId) return;

    setIsLoading(true);

    const settings = Object.entries(formValues.settings).map(([settingId, optionIds]) => {
      const s = contentSettings.data?.find(({ id }) => id === settingId);
      const newOptions = s?.options?.filter((o) => optionIds.includes(o.id));

      return {
        id: settingId,
        title: s?.title ?? "",
        type: s?.type ?? "",
        options: newOptions ?? { id: "", title: "" },
        min: s?.min ?? 0,
        max: s?.max ?? 0,
      };
    }) as Setting[];

    const dish = {
      ...selectedMenuItem,
      ...formValues,
      settings,
      id: v4(),
    };

    await addItems([dish as unknown as OrderItem]);

    setIsLoading(false);
    handleClose();
  };

  const initialValues = useMemo(() => {
    const draft = {
      quantity: 1,
      settings: {} as Record<string, string>,
      comments: "",
    };

    if (contentSettings?.data) {
      contentSettings.data.forEach((s) => {
        if (s?.defaultOption) {
          draft.settings[s.id] = s.defaultOption;
        }
      });
    }

    return draft;
  }, [contentSettings?.data]);

  //TODO: disabled content by content and branch settings
  const isDisabled = useMemo(() => {
    return Boolean(selectedMenuItem?.isDisabled) || Boolean(activeBranch && selectedMenuItem?.branchSettings?.[activeBranch]?.isDisabled);
  }, [selectedMenuItem?.isDisabled, selectedMenuItem?.branchSettings, activeBranch]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const itemId = qs.get("itemId");
    if (itemId) {
      const contentItem = publishedContent.find((c) => c.id === itemId);
      if (contentItem) {
        dispatch(openItem(contentItem));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedContent]);

  return (
    <>
      <Drawer
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : "50%",
            },
          },
        }}
        open={!!selectedMenuItem}
        onClose={handleClose}
        anchor="right"
        sx={{ position: "relative" }}
      >
        {selectedMenuItem && contentSettings?.data ? (
          <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues}>
            <>
              <Stack
                spacing={2}
                paddingX={2}
                direction={"row"}
                alignContent={"center"}
                justifyContent={"space-between"}
                width={"100%"}
                sx={{
                  position: "absolute",
                  zIndex: 100,
                  top: 24,
                }}
              >
                <Stack>
                  <IconButton onClick={handleClose}>
                    <HugeiconsIcon icon={Cancel01Icon} />
                  </IconButton>
                </Stack>

                {isLoggedIn ? (
                  <Stack direction={"row"} spacing={2} alignContent={"center"}>
                    <IconButton
                      disabled={updateContentMutation.isLoading}
                      onClick={() => {
                        updateContent({
                          organizationId,
                          contentId: selectedMenuItem.id,
                          content: {
                            isDisabled: !isDisabled,
                            ...(activeBranch && {
                              branchSettings: {
                                ...selectedMenuItem?.branchSettings,
                                [activeBranch]: {
                                  ...selectedMenuItem?.branchSettings?.[activeBranch],
                                  isDisabled: !isDisabled
                                }
                              }
                            })
                          }
                        })
                      }}
                      sx={{
                        backgroundColor: (theme) => isDisabled ? theme.palette.error.main : theme.palette.success.main
                      }}
                    >
                      <HugeiconsIcon icon={isDisabled ? ViewOffIcon : ViewIcon} color="white" />
                    </IconButton>
                  </Stack>
                ) : null}
                {/* {location.pathname.includes("/menus/") ? ( */}
                {/* <IconButton onClick={onBack}>
                      <ChevronLeft />
                    </IconButton>

                    <IconButton onClick={onContinue}>
                      <ChevronRight />
                    </IconButton> */}
                {/* ) : null} */}
              </Stack>

              <RestaurantForm dish={selectedMenuItem} settings={contentSettings?.data} isLoading={isLoading} />
            </>
          </Formik>
        ) : null}
      </Drawer>
      <ContactForm />
    </>
  );
};

export default ProductDetails;
